<template>
    <div class="multi-step-filter-item f fa-c"
        :key="item.value"
        :class="[
            isSelected && !engine.isMultiMode ? 'el--active' : '',
            canSingleSelect || canClickRowForSelect ? 'c-p' : '',
            isResticted ? 'restricted' : '',
            showHover ? 'show-hover' : '']"
        :title="uiTooltip"
        @click="onItemSelected">
        <FormCheckbox v-if="showCheckmark"
            size="s"
            display="check"
            v-model="checked"
            :intermediate="isPartialSelected && !engine.isolateSelection"
            :disabled="!canCheck"
            @input="onCheckChanged" />
        <div class="f fa-l fv sm-l-m el-title">
            <div v-if="qstring.length > 0" class="te-subnote el-breadcrumbs">{{ breadcrumbs }}</div>
            <div class="fg">{{ item.label | athT }}</div>
            <TruncatedText class="te-subnote dw-10" :uiText="item.subtitle" />
        </div>
        <div class="fg"></div>
        <VisualBadge v-if="item.badge.length > 0" :lbl="item.badge" :cname="item.badge_style" />
        <VisualBadge v-if="showBadgeCount" :lbl="badgeCount" class="sm-l-s" />
        <ButtonIcon v-if="item.isGroup && !canClickRowForSelect"
            class="el--transparent el--s-s sm-l-s"
            icon="chevron-right"
            @click.prevent.stop="onOpenGroup" />
        <VisualIcon v-if="item.isGroup && canClickRowForSelect"
            class="sm-l-s"
            name="chevron-right" />
    </div>
</template>
<script>
import FormCheckbox from '../../atoms/forms/FormCheckbox.vue';
import ButtonIcon from '../../atoms/ButtonIcon.vue';
import VisualBadge from '../../atoms/visuals/Badge.vue';
import VisualIcon from '../../atoms/visuals/Icon.vue';
import TruncatedText from '../../atoms/visuals/TruncatedText.vue';

export default {
    components: {FormCheckbox, ButtonIcon, VisualBadge, VisualIcon, TruncatedText},
    props: {
        /**
         * The current search query string. When non-empty, breadcrumbs are displayed above the
         * item's label to show its position in the item hierarchy.
         */
        qstring: {type: String, default: ''},
        /**
         * The multi-step filter item model rendered by this row, including its label, subtitle,
         * badge, and whether it is a group (which opens into a new step rather than being checked
         * directly).
         */
        item: {type: Object, default: () => {}},
        /**
         * Whether to show a badge with the count of selected descendants next to a group item.
         */
        showBadgeCounts: {type: Boolean, default: true},
        /**
         * The filter engine instance that tracks selection state and provides helpers such as
         * isSelected, isPartiallySelected, and isRestrictedItem.
         */
        engine: {type: Object, default: () => {}}
    },
    data() {
        return {
            checked: this.item.calculateCheckedState(this.engine)
        };
    },
    methods: {
        onItemSelected: function (event) {
            if (this.canClickRowForSelect) {
                event.preventDefault();
                event.stopPropagation();
                if (this.item.isGroup)
                    this.onOpenGroup();
                else if (this.canCheck) {
                    this.checked = !this.checked;
                    this.onCheckChanged();
                }
                return;
            }
            if (!this.canSingleSelect) return;
            event.preventDefault();
            event.stopPropagation();
            this.$emit('onItemSelected', this.item);
        },
        onOpenGroup: function () {
            this.$emit('onOpenGroup', this.item);
        },
        onCheckChanged: function () {
            this.$emit('onCheck', this.item.getCheckedList(this.checked, this.engine));
        }
    },
    computed: {
        uiTooltip: function () {
            return this.isResticted ? MeadCommon.I18N.go('mead.x_is_already_selected', {value: this.item.label})
                : this.item.label;
        },
        canClickRowForSelect: function () {
            return this.item.canClickRowForSelect(this.engine);
        },
        isSelected: function () {
            return this.engine.isSelected(this.item);
        },
        isPartialSelected: function () {
            return this.engine.isPartiallySelected(this.item);
        },
        isResticted: function () {
            return this.engine.isRestrictedItem(this.item);
        },
        showHover: function () {
            return this.canSingleSelect || (this.canCheck && this.showCheckmark) || this.canClickRowForSelect
        },
        canSingleSelect: function () {
            return this.item.canSingleSelect(this.engine);
        },
        showCheckmark: function () {
            return this.item.showCheckmark(this.engine);
        },
        breadcrumbs: function () {
            let label = '';
            for (const crumb of this.item.breadcrumbs) {
                if (label.length !== 0) label += ' > '
                label += MeadCommon.I18N.go(crumb.label);
            }
            return label;
        },
        /**
         * If we can check or uncheck the item
         */
        canCheck: function () {
            return this.item.canToggleCheckedState(this.engine, this.checked);
        },
        showBadgeCount: function () {
            return this.showBadgeCounts && this.engine.isMultiMode && this.item.isGroup && this.badgeCount > 0;
        },
        badgeCount: function () {
            return this.item.getBadgeCount(this.engine);
        }
    },
    watch: {
        engine: {
            deep: true,
            handler: function () {
                this.checked = this.item.calculateCheckedState(this.engine)
            }
        }
    }
};
</script>
<style lang="scss" scoped>
@import '../../../../sass/_variables.scss';
.multi-step-filter-item {
    letter-spacing: $ls-s;
    border-radius: $br-m;
    padding: 0.8rem $s-m;
    line-height: $lh-m;
    white-space: normal;
    transition: background-color 0.5s ease;

    .el-breadcrumbs {
        text-wrap: auto;
    }

    &.restricted {
        opacity: .5;
    }

    &.show-hover {
        &:not(.el--active):active,
        &:not(.el--active):hover {
            background-color: var(--c-brand-light-bg);

            .te-subnote {
                color: var(--c-brand-light-fg);
            }
        }
    }

    &.el--active {
        background-color: var(--c-brand-default-bg);
    }

    .el-title {
        min-width: 5%;
    }
}
</style>
